import React, { useMemo, useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { ImageParams } from 'components/Common/ResponsiveImage'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { isHotelPackage } from 'lib/offer/offerTypes'
import { NO_IMAGE_AVAILABLE_ID } from 'constants/image'
import ImageWithReservedSpace from 'components/Common/ImageWithReservedSpace'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useLowestPackagePrices from 'components/OfferPage/hooks/useLowestPackagePrices'
import OfferPageRoomGallery from 'components/OfferPage/OfferPageRoomGallery'
import { ImageChangeHandler } from 'LuxKit/Carousel/BaseImageCarousel'
import { carouselImageImpressionEvent } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'

export const AccommodationRoomTileImageContainer = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.seven};
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
`

const IMAGE_PROPS: ImageParams = {
  mobileWidth: '100vw',
  tabletWidth: '100vw',
  desktopWidth: '400px',
  largeDesktopWidth: '400px',
  quality: 'good',
  fit: 'center',
}

interface Props {
  roomPackage: App.HotelPackage | App.BedbankPackage
  offer: App.HotelOffer | App.BedbankOffer
  previewMode?: boolean
  packageViews?: Array<OfferPackageOptionView>
  /** @default true */
  showBookmark?: boolean
}

function AccommodationRoomTileImage(props: Props) {
  const {
    roomPackage,
    offer,
    packageViews,
    previewMode,
    showBookmark = true,
  } = props

  const room = isHotelPackage(roomPackage) ? roomPackage.roomType : roomPackage
  const roomId = isHotelPackage(roomPackage) ? roomPackage.roomType.id : roomPackage.roomId

  const availablePackageViews = useMemo(() => packageViews?.filter(view => view.capacityValid && view.hotelAvailable), [packageViews])
  const rateOptionViews = previewMode ? packageViews : availablePackageViews
  const { price, value } = useLowestPackagePrices(rateOptionViews)
  const listId = useMemo(() => uuidv4(), [])

  const handleImageChange = useCallback<ImageChangeHandler>((index, image) => {
    if (offer && image) {
      Analytics.trackEvent(
        carouselImageImpressionEvent(
          image,
          index + 1,
          'offer-package-gallery',
          listId,
          offer,
          roomId,
        ),
      )
    }
  }, [offer, roomId, listId])

  return <AccommodationRoomTileImageContainer>
    {!!room.images.length && <OfferPageRoomGallery
      room={room}
      imageParams={IMAGE_PROPS}
      onImageChange={handleImageChange}
      topRight={showBookmark && <CSSBreakpoint max="tablet">
        <TripGuard>
          <BookmarkButton withLabel="tablet" offer={offer} roomTypeId={roomId} price={price} value={value} />
        </TripGuard>
      </CSSBreakpoint>}
    />}
    {!room.images.length && <ImageWithReservedSpace
      id={NO_IMAGE_AVAILABLE_ID}
      {...IMAGE_PROPS}
      alt="Sorry this image is unavailable"
    />}
  </AccommodationRoomTileImageContainer>
}

export default withOfferPageAndReduxState((offerPageState, appState) => ({
  previewMode: appState.system.previewMode,
  offer: (offerPageState.offer ?? offerPageState.bedbankOffer)!,
}), AccommodationRoomTileImage)
